/**
 * ConfirmDialog — ACM-styled potvrzovací dialog (nahrazuje native window.confirm()).
 *
 * Nativní confirm() vypadá mimo design systém a v některých embedded/webview
 * kontextech se chová nespolehlivě (Filip 2026-07-31: "to menu bylo hnusný a
 * ne ACM systémový"). Použij misto něj.
 *
 * Použití:
 *   const [pending, setPending] = useState<string | null>(null);
 *   <ConfirmDialog
 *     open={!!pending}
 *     title="Smazat instanci?"
 *     description={`Opravdu smazat "${pending}"?`}
 *     confirmLabel="Smazat"
 *     danger
 *     onConfirm={() => { onDelete(pending!); setPending(null); }}
 *     onCancel={() => setPending(null)}
 *   />
 */
import { useTranslation } from 'react-i18next';
import { Modal } from './Modal';
import { Button } from './Button';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel,
  danger = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const { t } = useTranslation();
  const ok = confirmLabel ?? t('ui.confirm');
  const cancel = cancelLabel ?? t('ui.cancel');
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      size='sm'
      footer={
        <>
          <Button variant='ghost' size='sm' onClick={onCancel}>
            {cancel}
          </Button>
          <Button variant={danger ? 'danger' : 'primary'} size='sm' onClick={onConfirm}>
            {ok}
          </Button>
        </>
      }
    >
      {description && <p className='text-sm text-gray-400'>{description}</p>}
    </Modal>
  );
}
